/**
 * Prototyper Service
 *
 * Handles prototype creation and management for conversations
 */

import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import type {
  PrototypeCreationResponse,
  UpdatePrototyperDemoLinkParams,
  PrototyperBySlugResponse,
  PrototypeSnapshotPrepareResponse,
  PrototypeSnapshotFinalizeResponse,
  PrototypeSnapshotLatestResponse,
  PrototypePublishResponse,
} from '../../types/prototyper.types';

class PrototyperService {
  /**
   * Update a prototyper demo link (slug and/or header)
   */
  async updatePrototyperDemoLink(
    params: UpdatePrototyperDemoLinkParams
  ): Promise<PrototypeCreationResponse> {
    try {
      const { id, slug } = params;
      const url = ApiUrls.updatePrototyperDemoLink(id);

      const payload: any = {};
      if (slug !== undefined) payload.slug = slug;

      const response = await apiClient.patch<PrototypeCreationResponse>(url, payload);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get prototyper demo link by slug
   */
  async getPrototyperBySlug(slug: string): Promise<PrototyperBySlugResponse> {
    try {
      const url = ApiUrls.getPrototyperBySlug(slug);
      const response = await apiClient.get<PrototyperBySlugResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get prototyper demo link by ID
   */
  async getPrototyperById(id: string | number): Promise<PrototypeCreationResponse> {
    try {
      const url = ApiUrls.getPrototyperById(id);
      const response = await apiClient.get<PrototypeCreationResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Presigned PUT upload for conversation prototype snapshot (zip).
   */
  async preparePrototypeSnapshot(
    projectId: string,
    conversationId: string,
    llmInteractionId?: string
  ): Promise<PrototypeSnapshotPrepareResponse> {
    try {
      const url = ApiUrls.prototypeSnapshotPrepare(projectId, conversationId);
      const response = await apiClient.post<PrototypeSnapshotPrepareResponse>(url, {
        ...(llmInteractionId ? { llmInteractionId } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async finalizePrototypeSnapshot(
    projectId: string,
    conversationId: string,
    body: {
      snapshotId: string;
      etag?: string;
      sizeBytes?: number;
      llmInteractionId?: string;
      buildVerified?: boolean;
    }
  ): Promise<PrototypeSnapshotFinalizeResponse> {
    try {
      const url = ApiUrls.prototypeSnapshotFinalize(projectId, conversationId);
      const response = await apiClient.post<PrototypeSnapshotFinalizeResponse>(url, body);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Build zip on the client, presign PUT to S3 (no axios on upload URL), then finalize.
   */
  async uploadPrototypeSnapshotZip(
    projectId: string,
    conversationId: string,
    zipBlob: Blob,
    llmInteractionId?: string,
    buildVerified?: boolean
  ): Promise<PrototypeSnapshotFinalizeResponse | void> {
    if (zipBlob.size === 0) return;
    const prepare = await this.preparePrototypeSnapshot(projectId, conversationId, llmInteractionId);
    if (
      !prepare?.snapshotId ||
      !prepare.uploadUrl ||
      typeof prepare.maxBytes !== 'number'
    ) {
      throw new Error('Invalid snapshot prepare response from server');
    }
    if (zipBlob.size > prepare.maxBytes) {
      throw new Error(
        `Prototype snapshot too large (${zipBlob.size} bytes; max ${prepare.maxBytes})`
      );
    }
    const putRes = await fetch(prepare.uploadUrl, {
      method: 'PUT',
      body: zipBlob,
      headers: { 'Content-Type': 'application/zip' },
    });
    if (!putRes.ok) {
      throw new Error(`Snapshot upload failed (${putRes.status})`);
    }
    const rawEtag = putRes.headers.get('etag');
    const etag =
      rawEtag != null ? rawEtag.replace(/^W\//, '').replace(/"/g, '') || undefined : undefined;
    return this.finalizePrototypeSnapshot(projectId, conversationId, {
      snapshotId: prepare.snapshotId,
      etag,
      sizeBytes: zipBlob.size,
      ...(llmInteractionId ? { llmInteractionId } : {}),
      ...(buildVerified !== undefined ? { buildVerified } : {}),
    });
  }

  async getPrototypeSnapshotByInteraction(
    projectId: string,
    conversationId: string,
    llmInteractionId: string
  ): Promise<PrototypeSnapshotLatestResponse> {
    try {
      const url = ApiUrls.prototypeSnapshotByInteraction(projectId, conversationId, llmInteractionId);
      const response = await apiClient.get<PrototypeSnapshotLatestResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createPrototyperBuildSession(
    llmInteractionId: string,
    body: { prototypeDisplayName?: string; buildTimeline?: Record<string, unknown> }
  ): Promise<{ id: string }> {
    const response = await apiClient.post(ApiUrls.prototypingBuildSessionLlm(llmInteractionId), body);
    return response.data;
  }

  async patchPrototyperBuildSession(
    llmInteractionId: string,
    body: Record<string, unknown>
  ): Promise<void> {
    await apiClient.patch(ApiUrls.prototypingBuildSessionLlm(llmInteractionId), body);
  }

  async createTmpPrototyperBuildSession(
    tmpLlmInteractionId: string,
    body: { prototypeDisplayName?: string; buildTimeline?: Record<string, unknown> }
  ): Promise<{ id: string }> {
    const response = await apiClient.post(ApiUrls.prototypingBuildSessionTmp(tmpLlmInteractionId), body);
    return response.data;
  }

  async patchTmpPrototyperBuildSession(
    tmpLlmInteractionId: string,
    body: Record<string, unknown>
  ): Promise<void> {
    await apiClient.patch(ApiUrls.prototypingBuildSessionTmp(tmpLlmInteractionId), body);
  }

  async getPrototypeSnapshotIndex(
    projectId: string,
    conversationId: string
  ): Promise<
    Array<{
      llmInteractionId: string;
      snapshotId: string;
      revision: number;
      buildVerified?: boolean;
      createdAt: string;
      prototyperDemoLinkId?: string;
    }>
  > {
    try {
      const url = ApiUrls.prototypeSnapshotIndex(projectId, conversationId);
      const response = await apiClient.get<
        Array<{
          llmInteractionId: string;
          snapshotId: string;
          revision: number;
          buildVerified?: boolean;
          createdAt: string;
          prototyperDemoLinkId?: string;
        }>
      >(url);
      return response.data ?? [];
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Trusted publish: builds snapshot on server and deploys static assets to demo CDN/S3.
   */
  async publishPrototype(
    projectId: string,
    conversationId: string,
    body: { slug: string; snapshotId?: string }
  ): Promise<PrototypePublishResponse> {
    try {
      const url = ApiUrls.prototypePublish(projectId, conversationId);
      const response = await apiClient.post<PrototypePublishResponse>(url, body, {
        timeout: 600_000,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error.response) {
      const data = error.response.data;
      let message = data?.message;
      if (typeof message === 'object' && message !== null) {
        const detail = (message as any).detail || (message as any).message;
        message =
          typeof detail === 'string'
            ? detail
            : JSON.stringify(message).slice(0, 2000);
      }
      if (!message || typeof message !== 'string') {
        message = 'Request failed';
      }
      return new Error(message);
    }

    // Network error
    return new Error('Network error. Please check your connection.');
  }
}

export const prototyperService = new PrototyperService();
export default prototyperService;
